import creatorSdk from '@src/services/creator-sdk';
import {
    type CollectionReference,
    collection,
    type DocumentData,
    type DocumentReference,
    type DocumentSnapshot,
    doc,
    getDoc,
    getDocs,
    getFirestore,
    limit,
    type QueryConstraint,
    type QueryDocumentSnapshot,
    query,
    startAfter,
} from 'firebase/firestore/lite';
import { type Atom, atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { atomWithImmer } from 'jotai-immer';
import { jotaiStore } from '../store';
import type { createMapAtom } from './mapAtom';

export type LoadStatus = 'idle' | 'loading' | 'success' | 'error';

export interface BaseCollectionState<TEntity = unknown> {
    ids: string[];
    lastDoc: QueryDocumentSnapshot<TEntity> | null;
    hasMore: boolean;
    status: LoadStatus;
    error: Error | null;
}

export interface CreateCollectionAtomPayload<TParams, TEntity> {
    generateKey: (params: TParams) => string;
    equalityCheck: (a: TParams, b: TParams) => boolean;
    debugLabel: string;
}

export function createCollectionAtomFamily<TParams, TEntity = unknown>(config: CreateCollectionAtomPayload<TParams, TEntity>) {
    const { generateKey, equalityCheck, debugLabel } = config;

    return atomFamily((params: TParams) => {
        const key = generateKey(params);

        const initialState: BaseCollectionState<TEntity> = {
            ids: [] as string[],
            lastDoc: null as QueryDocumentSnapshot<TEntity> | null,
            hasMore: true,
            status: 'idle' as LoadStatus,
            error: null as Error | null,
        };

        const baseAtom = atomWithImmer(initialState);
        baseAtom.debugLabel = `${debugLabel}_${key}`;
        return baseAtom;
    }, equalityCheck);
}

export type CollectionAtomEntityType<TCollectionAtom> = TCollectionAtom extends Atom<BaseCollectionState<infer TEntity>> ? TEntity : never;

export type LoadMoreActionParams<TKeyParams = unknown> = TKeyParams & {
    fetchLimit?: number;
};

export function createLoadMoreAtom<TEntity = unknown, TKeyParams = unknown>(
    collectionAtomFamily: ReturnType<typeof createCollectionAtomFamily<TKeyParams, TEntity>>,
    entityName: string,
    getQueryConstraintsFn: (keyParams: TKeyParams) => QueryConstraint[],
    mapAtom: ReturnType<typeof createMapAtom<TEntity>>
) {
    const baseAtom = atom<null, [LoadMoreActionParams<TKeyParams>], void>(null, async (get, set, params: LoadMoreActionParams<TKeyParams>) => {
        const { fetchLimit = 20, ...specificKeyParams } = params;
        const collectionAtom = collectionAtomFamily(specificKeyParams as TKeyParams);
        const currentState = get(collectionAtom);

        const debugKey = (collectionAtom as { debugLabel?: string }).debugLabel || 'unknownCollection';

        if (currentState.status === 'loading' || !currentState.hasMore) {
            if (!currentState.hasMore) {
                console.log(`[LoadMoreAtom ${debugKey}] No more items to load.`);
            }
            return;
        }
        set(collectionAtom, draft => {
            draft.status = 'loading';
            draft.error = null;
        });

        try {
            const queryConstraints = getQueryConstraintsFn(specificKeyParams as TKeyParams);
            const lowerBound = currentState.lastDoc;
            const { ids, entitiesData, lastDoc, hasMore } = await fetchEntities<TEntity>(entityName, {
                fetchLimit,
                queryConstraints,
                lowerBound,
            });

            jotaiStore.set(mapAtom, draft => {
                Object.assign(draft, entitiesData);
            });

            set(collectionAtom, draft => {
                const existingIds = new Set(draft.ids);
                const newIds = ids.filter(id => !existingIds.has(id));
                draft.ids.push(...newIds);
                draft.lastDoc = lastDoc as QueryDocumentSnapshot<TEntity> | null;
                draft.hasMore = hasMore;
                draft.status = 'success';
                draft.error = null;
            });
        } catch (error: unknown) {
            console.error(`[LoadMoreAtom ${debugKey}] Error:`, error);
            set(collectionAtom, draft => {
                draft.status = 'error';
                draft.error = error instanceof Error ? error : new Error(String(error) || 'Unknown error');
            });
        }
    });
    baseAtom.debugLabel = `loadMore_${entityName}}`;
    return baseAtom;
}

export interface FetchEntitiesPayload<TEntity = unknown> {
    lowerBound?: DocumentSnapshot<TEntity> | null;
    fetchLimit?: number;
    queryConstraints?: QueryConstraint[];
}

export interface FetchEntitiesResult<TEntity = unknown> {
    ids: string[];
    entitiesData: Record<string, TEntity>;
    lastDoc: QueryDocumentSnapshot<TEntity, DocumentData> | null;
    hasMore: boolean;
}

export async function fetchEntity<T>(entityType: string, entityId: string) {
    const db = getFirestore(creatorSdk.getApp());
    const entityRef = doc(db, entityType, entityId) as DocumentReference<T>; // Use entityType as collection name
    const snapshot = await getDoc(entityRef);

    if (!snapshot.exists()) {
        return;
    }

    const data = snapshot.data();
    return { ...data, id: snapshot.id } as T;
}

export async function fetchEntities<T>(entityType: string, payload: FetchEntitiesPayload<T>) {
    const { lowerBound, fetchLimit = 20, queryConstraints = [] } = payload;

    const db = getFirestore(creatorSdk.getApp());
    const entitiesRef = collection(db, entityType) as CollectionReference<T>; // Use entityType as collection name
    const paginationConstraints = lowerBound ? [startAfter(lowerBound)] : [];
    const finalQuery = query(entitiesRef, ...queryConstraints, ...paginationConstraints, limit(fetchLimit));
    const snapshot = await getDocs(finalQuery);
    const ids: string[] = [];
    const entitiesData: Record<string, T> = {};
    for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const entityWithId = { ...data, id: docSnap.id } as T;
        entitiesData[docSnap.id] = entityWithId;
        ids.push(docSnap.id);
    }

    const lastDoc = (snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot<T>) ?? null;
    const hasMore = snapshot.size === fetchLimit;

    return { ids, entitiesData, lastDoc, hasMore } as FetchEntitiesResult<T>;
}
